import * as i0 from '@angular/core';
import { InjectionToken, inject, Component, isSignal, Injectable, APP_INITIALIZER, Injector, EventEmitter, DestroyRef, signal, NgZone, ErrorHandler, LOCALE_ID, ENVIRONMENT_INITIALIZER, makeEnvironmentProviders } from '@angular/core';
import { from, Observable, lastValueFrom, of, tap, catchError, throwError, BehaviorSubject, filter, switchMap, map, mergeMap as mergeMap$1, forkJoin } from 'rxjs';
import { getParamNames, BarCacheManager, BarCacheStore, BarCacheEntryOptions, BaasMeetpuntService, BarStopwatch, _OopsDomService, _OopsType, resolveValue, BaasAppState, BAAS_CUSTOM_EVENT, openUrl, BaasSecurityService, BaasI18n, concatUrl, BaasAuthService, BaasLoggingService, checkDateObject, convertToISOString, LOCALE, _BaasApm, BAAS_TYPES, _BarAppInfoManager, _BarXdc, BaasBootstrapOptions, _BaasContextManager, _BaasTransportHelper, extractUrlParts, BaasAuditType, BaasContextTransportModus, generateBaasAuthHeaders, checkUrlAbsolute, _checkCompatibleOrigin, ensureGlobal, BaasMeldingService, BaasAuditService, BaasToepassingService, generateUuid, _addAuditInfoOnDocument, BAAS_METADATA_KEY, BarCacheStoreType, BarNotificationManager, _BaasProfielService, BaasConfigurationService, BarIdpClient, BarGvkManager, _BaasStaticConfigurationApiClient } from '@lvflanders/bar';
import { HttpParams, HttpResponse, HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { Router, NavigationEnd, NavigationStart, NavigationError, DefaultUrlSerializer, UrlSerializer } from '@angular/router';
import { registerLocaleData, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { parseISO, isAfter, isBefore, isEqual } from 'date-fns';
import { tap as tap$1, mergeMap, catchError as catchError$1, delayWhen } from 'rxjs/operators';
import localeNlExtra from '@angular/common/locales/extra/nl';
import localeNlBeExtra from '@angular/common/locales/extra/nl-BE';
import localeNl from '@angular/common/locales/nl';
import localeNlBe from '@angular/common/locales/nl-BE';
import { Container } from 'inversify';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { afterFrame } from '@elastic/apm-rum-core';
import { saveAs } from 'file-saver';

// injection token to have same class injected in different ways
const BAR_GLOBAL_CACHE = new InjectionToken('Global cache store');
const BAR_USER_CACHE = new InjectionToken('User cache store');
const BAR_APP_CACHE = new InjectionToken('Application cache store');
const BAR_MELDINGEN_CACHE = new InjectionToken('Meldingen cache store');
/** Token for BAR initialized lifecycle hook. */
const BAR_INITIALIZED = new InjectionToken('BAR initialized token.');
/** Token for BAR error handler hook. */
const BAR_ERROR_HANDLER = new InjectionToken('BAR error handling token.');
/** Token used to define whether running under localhost. */
const BAR_LOCALHOST = new InjectionToken('BAR localhost token');
/** @deprecated Token for APM configuration. */
const BAAS_APM_CONFIG = new InjectionToken('BaaS APM configuratie');
/** Token for Telemetry service. */
const BAAS_TELEMETRY = new InjectionToken('BaaS Telemetry service');

/** Internal global variable to access BaaS Core context. */
const baasCoreContext = {
    injector: undefined
};

function defineCacheKey(cacheKeyPrefix, paramNames, args) {
    // no arguments just use cache key prefix (class + method)
    if (paramNames.length == 0) {
        return cacheKeyPrefix;
    }
    if (paramNames.length != args.length) {
        // we cannot match the parameters names with the arguments so we just stringify entire args
        return `${cacheKeyPrefix}_${JSON.stringify(args)}`;
    }
    // we have matching parameters, loop through them and generate a full cache key
    let result = cacheKeyPrefix;
    for (let i = 0; i < paramNames.length; i++) {
        result += `_${paramNames[i]}:${JSON.stringify(args[i])}`;
    }
    return result;
}
/**
 * Decorator to use to use on functions which return an observable which will use @lvflanders/bar caching.
 * Note that because it uses async cache it cannot be used on methods which do not return Observable.
 * @param options
 */
function BarCacheable(options) {
    return function (target, key, descriptor) {
        // original method
        const originalMethod = descriptor.value;
        const className = target.constructor != null ? target.constructor.name : null;
        const cacheKeyPrefix = options == null || options.key == null ? `${className}_${key}` : options.key;
        const paramNames = getParamNames(descriptor.value);
        descriptor.value = function (...args) {
            const cacheKey = defineCacheKey(cacheKeyPrefix, paramNames, args);
            let cacheStore;
            if (options != null && options.store != null) {
                if (options.store instanceof InjectionToken) {
                    // we have an injection token use this
                    cacheStore = baasCoreContext.injector.get(options.store);
                }
                else {
                    const manager = baasCoreContext.injector.get(BarCacheManager);
                    cacheStore = manager.getStore(options.store);
                }
            }
            else {
                // get the default one which is for the current app
                cacheStore = baasCoreContext.injector.get(BarCacheStore);
            }
            if (options != null && options.promise) {
                // consumer wants to use a promise return type
                return cacheStore.getOrSetItem(cacheKey, () => {
                    // callback in case not in cache yet
                    // get original result and we make assumption that an observable is used because this is what is mostly used in Angular applications
                    const originalResult = originalMethod.apply(this, args);
                    if (originalResult instanceof Promise) {
                        // convert it back to a promise because @lvflanders/bar uses promises to be more loosely coupled
                        return originalResult;
                    }
                    else {
                        Promise.reject('Functions using BarCacheable from @lvflanders/bar-ng decorator must return a Promise in case BarCacheableOptions.promise equals true.');
                    }
                }, options != null ? options.options : BarCacheEntryOptions.default);
            }
            else {
                // consumer wants to use default Observable return type
                // get the item through the cache and converts it to observable because we cannot detect at runtime what the return type of a method is without executing it
                return from(cacheStore.getOrSetItem(cacheKey, () => {
                    // callback in case not in cache yet
                    // get original result and we make assumption that an observable is used because this is what is mostly used in Angular applications
                    const originalResult = originalMethod.apply(this, args);
                    if (originalResult instanceof Observable) {
                        // convert it back to a promise because @lvflanders/bar uses promises to be more loosely coupled
                        return lastValueFrom(originalResult);
                    }
                    else {
                        Promise.reject('Functions using BarCacheable from @lvflanders/bar-ng decorator must return an Observable in case BarCacheableOptions.promise is not equal true.');
                    }
                }, options != null ? options.options : BarCacheEntryOptions.default));
            }
        };
        return descriptor;
    };
}

// https://netbasal.com/inspiration-for-custom-decorators-in-angular-95aeb87f072c
/**
 * Measures the duration of a directive or component (which is a directive) between ngOnInit and ngAfterViewInit.
 */
function BarMeetpunt() {
    return function (constructor) {
        // get meetpunt service through static injector on the core module
        const meetpunt = baasCoreContext.injector.get(BaasMeetpuntService);
        const ngOnInit = constructor.prototype.ngOnInit;
        let stopwatch = null;
        // override the ngOnInit
        constructor.prototype.ngOnInit = function (...args) {
            // create and start new watch
            stopwatch = new BarStopwatch();
            stopwatch.start();
            // call original ngOnInit after starting the watch (if present)
            ngOnInit && ngOnInit.apply(this, args);
        };
        const ngAfterViewInit = constructor.prototype.ngAfterViewInit;
        // override the ngAfterViewInit
        constructor.prototype.ngAfterViewInit = function (...args) {
            // call original ngAfterViewInit if present
            ngAfterViewInit && ngAfterViewInit.apply(this, args);
            // stop the watch
            stopwatch.stop();
            // call meetpunt service to log this
            meetpunt.log({
                meetpunt: 'DIRECTIVE',
                meetpuntDetail: constructor.name,
                waarde: stopwatch.duration,
                meetpuntInfo: document.location.href
            });
            stopwatch = null;
        };
    };
}

function barPageNotFoundGuard() {
    return (route, state) => {
        if (!route.routeConfig?.component)
            return true;
        const oopsDomService = inject(_OopsDomService);
        oopsDomService.showOops({ type: _OopsType.NotFound });
        return true;
    };
}

/** Purpose of component is to be able to trigger the route. */
class BarPageNotFoundComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarPageNotFoundComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: BarPageNotFoundComponent, isStandalone: true, selector: "bar-page-not-found", ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarPageNotFoundComponent, decorators: [{
            type: Component,
            args: [{
                    // no html, barPageNotFoundGuard is used to trigger oops
                    template: '',
                    standalone: true,
                    selector: 'bar-page-not-found'
                }]
        }] });

/**
 * Will return an Observable based on a value which can be of different types.
 * @param value
 */
function resolveValue$(value) {
    if (value instanceof Observable) {
        return value;
    }
    if (value instanceof Promise) {
        return from(value);
    }
    const resolvedValue = resolveValue(value);
    return of(resolvedValue);
}
function resolveSignalValue(value) {
    return isSignal(value) ? value() : value;
}
/**
 * Convers an object to a http params.
 * @param obj
 */
// https://github.com/angular/angular/issues/18012
function convertToHttpParams(obj) {
    let params = new HttpParams();
    if (obj != null) {
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const val = obj[key];
                if (val !== null && val !== undefined) {
                    params = params.append(key, val.toString());
                }
            }
        }
    }
    return params;
}
/** Creates the default base href factory. The base href factory generated will ensure the base href match is done in a case insensitive way. Use this when providing APP_BASE_HREF.*/
function createDefaultBaseHrefFactory(baseHref) {
    return () => {
        if (!baseHref || baseHref === '/')
            return baseHref;
        // do not take into account end slash because pathname is not with end slash
        const comparisonBaseHref = baseHref.endsWith('/') ? baseHref.substring(0, baseHref.length - 1) : baseHref;
        if (!window.location.pathname.toLowerCase().startsWith(comparisonBaseHref.toLowerCase()))
            return baseHref;
        return window.location.pathname.substring(0, comparisonBaseHref.length);
    };
}
function createPageNotFoundRoute(path) {
    return {
        path,
        component: BarPageNotFoundComponent,
        canActivate: [barPageNotFoundGuard()],
        data: { _framework: true }
    };
}

const PAGES_ROOT_PATH = 'bui/';
const STORAGE_KEY_PREFIX = 'lvflanders.bar.ng.core.';
const BAR_CORE_CONSTANTS = {
    STORAGE_KEY_PREFIX,
    PAGES: {
        ONBESCHIKBAAR: PAGES_ROOT_PATH + 'blokkerende-melding',
        SESSION_EXPIRED: PAGES_ROOT_PATH + 'session-expired',
        INSUFFICIENT_PRIVILEGES: PAGES_ROOT_PATH + 'insufficient-privileges',
        LOG_OFF: PAGES_ROOT_PATH + 'log-off'
    }
};

class BarRoutingService {
    #router = inject(Router);
    /**
     * Will navigates to a given url and make sure the current component is not reused in case of a same route navigation (reload).
     * @param url
     */
    async navigateByUrlWithReload(url, extras) {
        // navigate to the bar refresh route to then navigate to the same url (tricks of the trade)
        await this.#router.navigateByUrl('/bar-refresh', { skipLocationChange: true });
        // to then navigate back to the same page
        return await this.#router.navigateByUrl(url);
    }
    /**
     * Will navigates based on command array and make sure the current component is not reused in case of a same route navigation (reload).
     * @param url
     */
    async navigateWithReload(commands, extras) {
        // navigate to the bar refresh route to then navigate to the same url (tricks of the trade)
        await this.#router.navigateByUrl('/bar-refresh', { skipLocationChange: true });
        // to then navigate back to the same page
        return await this.#router.navigate(commands, extras);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarRoutingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarRoutingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarRoutingService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class BarCoreService {
    #injector;
    #appState;
    #hubMessage;
    #basiscontextChanged;
    // avoid cyclic dependency
    get #router() {
        return this.#injector.get(Router);
    }
    get #routing() {
        return this.#injector.get(BarRoutingService);
    }
    // avoid cyclic dependency
    get #appInits() {
        return this.#injector.get(APP_INITIALIZER);
    }
    constructor() {
        this.#injector = inject(Injector);
        this.#appState = inject(BaasAppState, { optional: true });
        /** Global core event that is invoked when an error occurs. */
        this.errorOccurred = new EventEmitter();
        /** Event invoked when performed soft refresh. */
        this.refreshed = new EventEmitter();
        /** Occurs when a custom message is send from the BaaS hub and received in the client. */
        this.customHubMessageReceived = new EventEmitter();
        this.basiscontextChanged = new EventEmitter();
        this.#hubMessage = (event) => this.customHubMessageReceived.emit(event.detail);
        this.#basiscontextChanged = (event) => this.basiscontextChanged.emit(event.detail);
        // Listen for the custom event on a JavaScript object or DOM element
        window.addEventListener(BAAS_CUSTOM_EVENT.CUSTOM_HUB_MESSAGE, this.#hubMessage);
        window.addEventListener(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, this.#basiscontextChanged);
    }
    ngOnDestroy() {
        window.removeEventListener(BAAS_CUSTOM_EVENT.CUSTOM_HUB_MESSAGE, this.#hubMessage);
        window.removeEventListener(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, this.#basiscontextChanged);
        this.basiscontextChanged.complete();
        this.customHubMessageReceived.complete();
        this.errorOccurred.complete();
        this.refreshed.complete();
    }
    /** Performs a soft refresh (make sure to have onSameUrlNavigation set to 'reload' in the routing setup) */
    async refresh() {
        // https://dormoshe.io/articles/hook-into-angular-initialization-process-8
        // get all async promises (or executed if no promise)
        if (this.#appInits) {
            for (const appInit of this.#appInits) {
                const initResult = appInit();
                await Promise.resolve(initResult);
            }
        }
        // after executing all startup logic
        // save url before navigating (otherwise wrong one)
        const url = this.#router.url;
        // use custom navigate with to reload to ensure route component is completely reloaded
        await this.#routing.navigateWithReload([url]);
        this.refreshed.emit();
    }
    /**
     * Navigate to the defined home url (default is startscherm)
     * @param invokeHomeAction Whether to invoke the custom home action defined in this service as a property.
     */
    async navigateHome(invokeHomeAction = true) {
        await this.#navigateAction(invokeHomeAction, () => {
            window.location = this.#appState.config.homeUrl;
        }, this.homeAction, undefined);
    }
    async navigateRoot(invokeRootAction = true, event = undefined) {
        await this.#navigateAction(invokeRootAction, async () => {
            const newTab = event && (event.ctrlKey || event.metaKey);
            if (newTab) {
                openUrl(this.#appState.location.app.url, '_blank');
            }
            else {
                await this.#router.navigate(['./']);
            }
        }, this.rootAction, event);
    }
    async #navigateAction(invokeAction, defaultAction, action, event) {
        let defaultBehaviour = false;
        if (invokeAction && action != null) {
            if (action.action != null) {
                const result = action.action(event);
                if (result instanceof Promise) {
                    await result;
                }
            }
            else if (action.routerUrl != null) {
                const routerUrl = resolveValue(action.routerUrl);
                await this.#router.navigateByUrl(routerUrl);
            }
            else if (action.routerCommands != null) {
                const routerLink = resolveValue(action.routerCommands);
                await this.#router.navigate(routerLink);
            }
            else if (action.href != null) {
                const href = resolveValue(action.href);
                openUrl(href, action.target);
            }
            else {
                defaultBehaviour = true;
            }
        }
        else {
            defaultBehaviour = true;
        }
        if (defaultBehaviour)
            defaultAction();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarCoreService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarCoreService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarCoreService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

function barActieAllowedGuard(...acties) {
    return async () => {
        const security = inject(BaasSecurityService);
        const i18n = inject(BaasI18n);
        const appState = inject(BaasAppState);
        const core = inject(BarCoreService);
        const localhost = inject(BAR_LOCALHOST);
        // iterate all acties
        for (const actie of acties) {
            const result = await security.checkActieAllowedFromContext(actie);
            if (!result) {
                if (!localhost) {
                    // perform hard redirect on deployed environment
                    window.location.href = concatUrl(appState.location.baasStatic.url, BAR_CORE_CONSTANTS.PAGES.INSUFFICIENT_PRIVILEGES);
                }
                else {
                    // on localhost emit an error, do not perform hard redirect
                    const errorMessage = i18n.get('DOMAIN_BAAS_CORE.INSUFFICIENT_PRIVILEGES_FOR_ACTIE', {
                        actie: actie.actie,
                        includeVoorwie: actie.includeVoorwie,
                        includeHoedanigheid: actie.includeHoedanigheid
                    });
                    console.error(errorMessage);
                    const error = new Error(errorMessage);
                    core.errorOccurred.emit(error);
                }
                return false;
            }
        }
        return true;
    };
}

function barAuthGuard() {
    return () => {
        const authService = inject(BaasAuthService);
        return authService.authenticated ? of(true) : from(authService.authenticate());
    };
}

function barZoneGuard(...zones) {
    return async () => {
        const appState = inject(BaasAppState);
        const core = inject(BarCoreService);
        const i18n = inject(BaasI18n);
        const localhost = inject(BAR_LOCALHOST);
        let result = false;
        if (zones != null && zones.length > 0 && appState.basiscontext != null && appState.basiscontext.zone != null && appState.basiscontext.zone.length > 0) {
            result = zones.indexOf(appState.basiscontext.zone) > -1;
        }
        if (!result) {
            if (!localhost) {
                await core.navigateHome();
            }
            else {
                const errorMessage = i18n.get('DOMAIN_BAAS_CORE.ZONES_NOT_ALLOWED', { zones: zones.join() });
                console.error(errorMessage);
                const error = new Error(errorMessage);
                core.errorOccurred.emit(error);
            }
        }
        return result;
    };
}

/**
 * Actie object to check. This is needed by the BarActieAllowedGuard.
 */
class BarActieFromContext {
    constructor(actie) {
        this.includeVoorwie = true;
        this.includeHoedanigheid = true;
        if (actie != null) {
            this.actie = actie.actie;
            this.includeVoorwie =
                actie.includeVoorwie === undefined
                    ? true
                    : actie.includeVoorwie;
            this.includeHoedanigheid =
                actie.includeHoedanigheid === undefined
                    ? true
                    : actie.includeHoedanigheid;
        }
    }
}

/** Represents a definition of an action without UI or styling. */
class BarActionDef {
}

/** Options for the cacheable decorator. */
class BarCacheableOptions {
    constructor() {
        /** Whether to use a promise instead of an Observable. The reason is that we cannot guess the return type by default without executing it. */
        this.promise = false;
    }
}

/** Represents the strategy to use for converting date objects to strings. */
var BarDateTimeConversionStrategy;
(function (BarDateTimeConversionStrategy) {
    /** Converts the date to an iso string with the exact same date. */
    BarDateTimeConversionStrategy[BarDateTimeConversionStrategy["Iso"] = 0] = "Iso";
    /** Converts the date to an iso string in GMT 0. */
    BarDateTimeConversionStrategy[BarDateTimeConversionStrategy["IsoGmt"] = 1] = "IsoGmt";
})(BarDateTimeConversionStrategy || (BarDateTimeConversionStrategy = {}));

var BarMeldingType;
(function (BarMeldingType) {
    BarMeldingType["Opgelost"] = "OPGELOST";
    BarMeldingType["Informatief"] = "INFORMATIEF";
    BarMeldingType["Belangrijk"] = "BELANGRIJK";
    BarMeldingType["Onbeschikbaar"] = "ONBESCHIKBAAR";
    BarMeldingType["Banner"] = "BANNER";
})(BarMeldingType || (BarMeldingType = {}));

class BarMelding {
    convert(m) {
        const meldingTypeMapping = {
            20: BarMeldingType.Opgelost,
            40: BarMeldingType.Informatief,
            60: BarMeldingType.Belangrijk,
            80: BarMeldingType.Onbeschikbaar,
            90: BarMeldingType.Banner
        };
        const classicBannerProcessing = (m) => {
            // banner information is the same accross BAR en BaaS
            this.bannerTitle = m.bannerTitle;
            this.bannerUrl = m.bannerUrl;
            this.bannerUrlText = m.bannerUrlText;
            this.bannerImage = m.bannerImage;
        };
        if (m.baas === true) {
            //BaaS melding
            if (m.version === '2') {
                // V2
                const readField = (m, field) => m.fields?.find(f => f.name === field)?.content;
                Object.assign(this, {
                    id: m.meldingId,
                    startDate: m.validFrom,
                    endDate: m.validUntil,
                    meldingType: m.meldingType,
                    location: m.urlPattern,
                    content: readField(m, 'TEKST'),
                    title: readField(m, 'TITEL'),
                    zone: m.zone
                });
                if (this.meldingType === BarMeldingType.Banner) {
                    this.bannerTitle = readField(m, 'TITLE');
                    this.bannerUrl = readField(m, 'LINK_URL');
                    this.bannerUrlText = readField(m, 'LINK_CONTENT');
                    this.bannerImage = readField(m, 'IMAGE');
                }
            }
            else {
                Object.assign(this, {
                    id: m.meldingId,
                    startDate: m.validFrom,
                    endDate: m.validUntil,
                    meldingType: meldingTypeMapping[m._mtype],
                    location: m.urlPattern,
                    content: m.meldingText,
                    zone: m.zone
                });
                classicBannerProcessing(m);
            }
        }
        //BAR melding
        else {
            Object.assign(this, {
                id: m.id,
                startDate: m.startDatum,
                endDate: m.eindDatum,
                meldingType: meldingTypeMapping[m.meldingType],
                location: m.locatie,
                content: m.tekst,
                zone: m.zone
            });
            classicBannerProcessing(m);
        }
        return this;
    }
}

// https://stackoverflow.com/a/46078299/2174621
class BarHttpParams extends HttpParams {
    constructor(
    /**This means this is a request that should pass the interceptor but happens in background*/
    backgroundRequest = false, 
    /**This means interception should be ommited*/
    ommitInterceptor = false, options) {
        super(options);
        this.backgroundRequest = backgroundRequest;
        this.ommitInterceptor = ommitInterceptor;
    }
}

// https://medium.com/@amcdnl/global-error-handling-with-angular2-6b992bdfb59c
class BarErrorHandler {
    #core = inject(BarCoreService);
    #logging = inject(BaasLoggingService, { optional: true });
    #errorHandlerHooks = inject(BAR_ERROR_HANDLER, { optional: true });
    #telemetry = inject(BAAS_TELEMETRY, { optional: true });
    handleError(error) {
        if (error == null)
            return;
        if (typeof error === 'object') {
            error.handled = true;
        }
        let proceed = true;
        // process angular hooks
        if (this.#errorHandlerHooks && this.#errorHandlerHooks.length) {
            this.#errorHandlerHooks.forEach((hook) => {
                const args = {
                    handled: false,
                    error: error
                };
                hook.run(args);
                if (args.handled)
                    proceed = false;
            });
        }
        // a hook already handled the error, stop proceeding
        if (!proceed)
            return;
        // do something with the exception
        try {
            console.error(error);
            const getCircularReplacer = () => {
                const seen = new WeakSet();
                return (key, value) => {
                    if (typeof value === 'object' && value !== null) {
                        if (seen.has(value)) {
                            return;
                        }
                        seen.add(value);
                    }
                    return value;
                };
            };
            // put the error in session storage
            window.sessionStorage[BAR_CORE_CONSTANTS.STORAGE_KEY_PREFIX + 'error'] = JSON.stringify(error, getCircularReplacer());
            this.#core.errorOccurred.emit(error);
            if (this.#logging != null) {
                this.#logging.error({ error });
            }
            // telemetry is optional, for example polaris doesn't have telemetry
            if (this.#telemetry)
                this.#telemetry.handleError(error);
        }
        catch (error) {
            console.error(error);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorHandler, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorHandler }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorHandler, decorators: [{
            type: Injectable
        }] });

// NOTE implemented because of persisting caching issues with aspnet core fallback index.html action
// https://medium.com/@kamrankhatti/angular-lazy-routes-loading-chunk-failed-42b16c22a377
const CHUNK_FAILED_MESSAGE = /Loading chunk [\d]+ failed/;
class BarErrorChunkLoadFailedHook {
    /** Invoked by the error handler. */
    run(args) {
        if (args.error?.message && CHUNK_FAILED_MESSAGE.test(args.error.message)) {
            args.handled = true;
            console.warn('Chunk failed to load, reloading the page.');
            window.location.reload();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorChunkLoadFailedHook, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorChunkLoadFailedHook }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorChunkLoadFailedHook, decorators: [{
            type: Injectable
        }] });

class BarErrorExpressionChangedAfterCheckedHook {
    /** Invoked by the error handler. */
    run(args) {
        if (args.error &&
            // NOTE since ng 18 we get -100 code
            (args.error.code === '100' || args.error.code === -100) &&
            args.error.message &&
            args.error.message.indexOf('NG0100') > -1) {
            args.handled = true;
            console.error(args.error);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorExpressionChangedAfterCheckedHook, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorExpressionChangedAfterCheckedHook }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorExpressionChangedAfterCheckedHook, decorators: [{
            type: Injectable
        }] });

function baasTelemetryInterceptor(req, next) {
    const telemetry = inject(BAAS_TELEMETRY);
    const transaction = telemetry.startTransaction(`NG HTTP ${req.method} ${req.url}`, 'ng-http-client');
    const httpSpan = transaction.startSpan('Invoking http call with Angular HttpClient', 'ng-apm-interceptor');
    const end = () => {
        httpSpan?.end();
        transaction.end();
    };
    return next(req).pipe(tap(() => {
        end();
    }), catchError((error) => {
        telemetry.handleError(error);
        end();
        return throwError(() => error);
    }));
}

// NOTE based on https://gist.github.com/martinobordin/39bb1fe3400a29c1078dec00ff76bba9
const ISO_8601_REGEX = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/;
function barDateTimeInterceptor(conversionStrategy) {
    return (req, next) => {
        traverseInput(req.body);
        return next(req).pipe(tap$1((event) => {
            if (event instanceof HttpResponse) {
                const body = event.body;
                traverseOutput(body);
            }
        }));
    };
    function traverseInput(body) {
        traverse(body, (value) => checkDateObject(value), (value) => toISO(value), (body) => traverseInput(body));
    }
    function traverseOutput(body) {
        traverse(body, (value) => isIso8601(value), (value) => parseISO(value), (body) => traverseOutput(body));
    }
    function toISO(value) {
        switch (conversionStrategy) {
            case BarDateTimeConversionStrategy.IsoGmt:
                return value.toISOString();
            default:
            case BarDateTimeConversionStrategy.Iso:
                return convertToISOString(value);
        }
    }
    function traverse(body, matcher, converter, traverser) {
        if (body == null || typeof body !== 'object')
            return;
        if (Array.isArray(body)) {
            for (let i = 0; i < body.length; i++) {
                const value = body[i];
                if (matcher(value)) {
                    body[i] = converter(value);
                }
                else if (typeof value === 'object') {
                    traverser(value);
                }
            }
        }
        else {
            for (const key of Object.keys(body)) {
                const value = body[key];
                if (matcher(value)) {
                    body[key] = converter(value);
                }
                else if (typeof value === 'object') {
                    traverser(value);
                }
            }
        }
    }
    function isIso8601(value) {
        if (value === null || value === undefined)
            return false;
        return ISO_8601_REGEX.test(value);
    }
}

const BAAS_CORE_I18N = {
    GENERIC_ERROR: 'Er heeft zich een fout voorgedaan',
    INSUFFICIENT_PRIVILEGES_FOR_ACTIE: 'Onvoldoende rechten voor actie "{{actie}}" (includeVoorWie: {{includeVoorwie}} - includeHoedanigheid: {{includeHoedanigheid}})',
    ZONES_NOT_ALLOWED: 'Geen toegang voor zone(s): "{{zones}}"'
};

class BarEmptyComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarEmptyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: BarEmptyComponent, isStandalone: true, selector: "bar-empty", ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarEmptyComponent, decorators: [{
            type: Component,
            args: [{ template: '', standalone: true, selector: 'bar-empty' }]
        }] });

const CORE_ROUTES = [{ path: 'bar-refresh', component: BarEmptyComponent, data: { _framework: true } }, createPageNotFoundRoute('**')];
class BaasCoreInitService {
    #injector = inject(Injector);
    #router = inject(Router);
    #i18n = inject(BaasI18n);
    init() {
        baasCoreContext.injector = this.#injector;
        // register nl-BE
        registerLocaleData(localeNlBe, LOCALE.FLEMISH, localeNlBeExtra);
        registerLocaleData(localeNl, LOCALE.DUTCH, localeNlExtra);
        // set up translation system
        this.#i18n.addTranslations('DOMAIN_BAAS_CORE', BAAS_CORE_I18N);
        this.#router.config.push(...CORE_ROUTES);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasCoreInitService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasCoreInitService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasCoreInitService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/** Optional feature kind. */
var BaasCoreFeatureKind;
(function (BaasCoreFeatureKind) {
    /** Whether we want date & time interception. */
    BaasCoreFeatureKind[BaasCoreFeatureKind["DateTimeInterception"] = 0] = "DateTimeInterception";
    /** Whether we want a full app connected to the BaaS backend. */
    BaasCoreFeatureKind[BaasCoreFeatureKind["BaasApp"] = 1] = "BaasApp";
    /** Whether to use i18n features from @lvflanders/bar and connect them to Angular.
     * Note do not use this feature in case the BaaS App feature is used! */
    BaasCoreFeatureKind[BaasCoreFeatureKind["I18n"] = 2] = "I18n";
    /** Apm settings. */
    BaasCoreFeatureKind[BaasCoreFeatureKind["Apm"] = 3] = "Apm";
})(BaasCoreFeatureKind || (BaasCoreFeatureKind = {}));
/** Internal dictionary keeping the order of each enum member */
const _BAAS_CORE_FEATURE_ORDER = {
    [BaasCoreFeatureKind.DateTimeInterception]: 0,
    [BaasCoreFeatureKind.BaasApp]: 1,
    [BaasCoreFeatureKind.I18n]: 2,
    [BaasCoreFeatureKind.Apm]: 3
};

/** Token to resolve BaasCommonApiClient interface. */
const BAAS_COMMON_API_CLIENT = new InjectionToken('BaasCommonApiClient token.');

/** Angular APM service. */
class _BaasApmService {
    #apm = inject(_BaasApm);
    #router = inject(Router);
    #destroyRef = inject(DestroyRef);
    /** Initializes APM for Angular. */
    observe() {
        // init only observes, APM is normally setup in @lvflanders/bar
        if (!this.#apm.active)
            return;
        // NOTE based on https://github.com/elastic/apm-agent-rum-js/blob/main/packages/rum-angular/src/apm.service.ts
        // we didn't use this class because it was not Ivy compliant
        let transaction;
        this.#router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((event) => {
            const eventName = event.toString();
            if (eventName.indexOf('NavigationStart') >= 0) {
                const name = event.url;
                transaction = this.#apm.apm.startTransaction(name, 'route-change', {
                    managed: true,
                    canReuse: true
                });
            }
            else if (eventName.indexOf('NavigationError') >= 0) {
                transaction && transaction.detectFinish();
            }
            else if (eventName.indexOf('NavigationEnd') >= 0) {
                if (!transaction) {
                    return;
                }
                /**
                 * The below logic must be placed in NavigationEnd since
                 * the we depend on the current route state to get the path
                 *
                 * Even If there are any redirects, the router state path
                 * will be matched with the correct url on navigation end
                 *
                 * Traverse the activated route tree to figure out the nested
                 * route path
                 */
                const route = this.#router.routerState.root.firstChild;
                if (route) {
                    let child = route;
                    let path = '/' + child.routeConfig.path;
                    while (child) {
                        child = child.firstChild;
                        if (child && child.routeConfig) {
                            const currentPath = child.routeConfig.path;
                            /**
                             * Ignore empty path's in the route config
                             */
                            if (currentPath) {
                                path += '/' + currentPath;
                            }
                        }
                    }
                    transaction.name = path;
                }
                afterFrame(() => transaction.detectFinish());
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BaasApmService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BaasApmService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BaasApmService, decorators: [{
            type: Injectable
        }] });

/** Hook for setting up Elastic APM. */
class BaasApmInitHook {
    constructor() {
        this.#apm = inject(_BaasApmService);
        this._framework = true;
    }
    #apm;
    run() {
        this.#apm.observe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasApmInitHook, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasApmInitHook }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasApmInitHook, decorators: [{
            type: Injectable
        }] });

/** Error handler hooks which uses the error delegate system of @lvflanders/bar. */
class BarErrorDelegateHandlerHook {
    #container = inject(Container);
    run(args) {
        if (!this.#container.isBound(BAAS_TYPES.BarErrorDelegate))
            return;
        const delegates = this.#container.getAll(BAAS_TYPES.BarErrorDelegate);
        for (const delegate of delegates) {
            const delegateArgs = {
                handled: false,
                error: args.error
            };
            delegate.handle(delegateArgs);
            if (delegateArgs.handled)
                args.handled = true;
            if (args.handled)
                break;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorDelegateHandlerHook, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorDelegateHandlerHook }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarErrorDelegateHandlerHook, decorators: [{
            type: Injectable
        }] });

class BarCoreInterceptor {
    #state = inject(BaasAppState);
    #appInfo = inject(_BarAppInfoManager);
    #xdc = inject(_BarXdc);
    #options = inject(BaasBootstrapOptions);
    #baasContextManager = inject(_BaasContextManager);
    #meetpunt = inject(BaasMeetpuntService);
    #logging = inject(BaasLoggingService);
    #localhost = inject(BAR_LOCALHOST);
    #oopsDomService = inject(_OopsDomService);
    #transportHelper = inject(_BaasTransportHelper);
    constructor() {
        this.#defineGlobal();
    }
    // defines if we handle the forbidden status (combined means we check different parts of the afschakelplan)
    get #handleAuthFailedStatusCombined() {
        // we handle it if defined globally through configuration and if the user didn't turn it off (if we don't have the configuration yet we assume we want to handle: init call)
        return this.#handleAuthFailedStatus && (!this.#state.config || this.#state.config.interception?.handleAuthFailed);
    }
    // gets if we handle the forbidden status based on a global linked to the local storage
    get #handleAuthFailedStatus() {
        let result = true;
        const tmp = window.localStorage.getItem('lvflanders.bar.ng.core.interception.handleAuthFailed');
        if (tmp != null && tmp.length > 0) {
            result = JSON.parse(tmp);
        }
        return result;
    }
    // sets if we handle the forbidden status based on a global linked to the local storage
    set #handleAuthFailedStatus(value) {
        window.localStorage.setItem('lvflanders.bar.ng.core.interception.handleAuthFailed', JSON.stringify(value));
    }
    // defines if we handle the forbidden status (combined means we check different parts of the afschakelplan)
    get #handleForbiddenStatusCombined() {
        // we handle it if defined globally through configuration and if the user didn't turn it off
        return this.#handleForbiddenStatus && this.#state.config?.interception?.handleForbidden;
    }
    // gets if we handle the forbidden status based on a global linked to the local storage
    get #handleForbiddenStatus() {
        let result = true;
        const tmp = window.localStorage.getItem('lvflanders.bar.ng.core.interception.handleForbidden');
        if (tmp != null && tmp.length > 0) {
            result = JSON.parse(tmp);
        }
        return result;
    }
    // sets if we handle the forbidden status based on a global linked to the local storage
    set #handleForbiddenStatus(value) {
        window.localStorage.setItem('lvflanders.bar.ng.core.interception.handleForbidden', JSON.stringify(value));
    }
    intercept(request, next) {
        const ommitInterceptor = request.params instanceof BarHttpParams && request.params.ommitInterceptor;
        if (ommitInterceptor) {
            return next.handle(request);
        }
        else {
            let stopwatch = null;
            // first we need to know if this is a background request, we don't want audit for audit (recursive)
            const backgroundRequest = request.params instanceof BarHttpParams && request.params.backgroundRequest;
            if (!backgroundRequest && this.#meetpunt.loggingEnabled) {
                stopwatch = new BarStopwatch();
                stopwatch.start();
            }
            // determine whether this is a domain request
            const domainRequest = this.#checkDomainRequest(request.url);
            const crossDomainRequest = this.#checkCrossDomainRequest(request.url);
            let promise = null;
            // for domain requests (within same application) or cross domain request, we want to persist the basiscontext or at least sync the cookies if enabled
            if (domainRequest || crossDomainRequest) {
                if (this.#state.basiscontext != null) {
                    // before we do the call we make sure the cookie is up to date for the request
                    promise = this.#appInfo.persistBasiscontext({
                        caller: 'Interceptor',
                        url: request.url
                    });
                }
                else if (crossDomainRequest && this.#xdc.enabled) {
                    promise = this.#xdc.sync({
                        caller: 'Interceptor',
                        url: request.url
                    });
                }
                // clone request
                request = request.clone({
                    // needed for localhost cookie sharing
                    withCredentials: true,
                    headers: this.#defineHeaders(request.headers, domainRequest, crossDomainRequest)
                });
            }
            if (promise != null) {
                // execute promise before proceeding
                // use merge map to be able
                return from(promise).pipe(mergeMap(() => {
                    return this.#handleRequest(request, next, stopwatch, domainRequest, crossDomainRequest);
                }));
            }
            else {
                // no promise just proceed
                return this.#handleRequest(request, next, stopwatch, domainRequest, crossDomainRequest);
            }
        }
    }
    #handleRequest(request, next, stopwatch, domainRequest, crossDomainRequest) {
        return next.handle(request).pipe(catchError$1((error) => {
            // 455 custom code authentication engine or authentication handler (in localhost we also want redirect behavior)
            if (this.#handleAuthFailedStatusCombined && !this.#localhost && error.status == 455) {
                this.#handleAuthFailed(error);
            }
            // 403 forbidden (e.g. actie allowed failed) handling, in localhost we don't want to get kicked out of application
            else if (this.#handleForbiddenStatusCombined && !this.#localhost && error.status == 403) {
                this.#handleForbidden(error);
            }
            return throwError(() => error);
        }), 
        // tap to execute some non asynchronous code after the request
        tap$1((ev) => {
            if (ev instanceof HttpResponse) {
                if (domainRequest && this.#options.auth?.syncBasiscontextAfterRequestCompletion) {
                    // get the basiscontext based on cookie and update basiscontext in temp state and page state
                    // a request can lead to changed basiscontext
                    const basiscontext = this.#appInfo.getBasiscontext();
                    const basiscontextKey = 'basiscontext';
                    this.#state[basiscontextKey] = basiscontext;
                }
                if (stopwatch != null) {
                    // read audit id response headers
                    const auditId = ev.headers.get('X-Audit-Id');
                    stopwatch.stop();
                    this.#meetpunt.log({
                        meetpunt: 'INTERCEPTOR',
                        meetpuntDetail: extractUrlParts(request.url).pathname,
                        waarde: stopwatch.duration,
                        meetpuntInfo: request.url,
                        auditId: auditId
                    });
                }
            }
        }), 
        // https://stackoverflow.com/a/52206312/2174621
        // here we execute another observable, but we don't alter the output which is needed by the return parameter of this function (delayWhen doesn't alter result)
        delayWhen((ev) => {
            // execute the sync promise in case it's a httpresponse (otherwise auto resolve)
            return from(ev instanceof HttpResponse && crossDomainRequest
                ? this.#xdc.sync({
                    caller: 'Interceptor-End',
                    url: request.url
                })
                : Promise.resolve());
        }));
    }
    #defineHeaders(headers, domainRequest, crossDomainRequest) {
        // needed for aspnet core knowing about ajax requests
        headers = headers.set('X-Requested-With', 'XMLHttpRequest');
        let requestToken = null;
        if (domainRequest && !crossDomainRequest && this.#state.localRequestToken != null && this.#xdc.enabled) {
            // local request cookie in case domain request && cross domain cookeis are enabled
            requestToken = this.#state.localRequestToken;
        }
        else if (this.#state.requestToken != null) {
            requestToken = this.#state.requestToken;
        }
        if (requestToken != null) {
            // antiforgery header
            headers = headers.set('X-XSRF-TOKEN', requestToken);
        }
        let auditId = null;
        let auditType = null;
        if (this.#state.routeId != null) {
            auditId = this.#state.routeId;
            auditType = BaasAuditType.Route;
        }
        else if (this.#state.pageId != null) {
            auditId = this.#state.pageId;
            auditType = BaasAuditType.Page;
        }
        else if (this.#state.sessionId != null) {
            auditId = this.#state.sessionId;
            auditType = BaasAuditType.Session;
        }
        if (auditId != null) {
            // set audit id so that audit system can chain everything together
            headers = headers.set('X-Audit-Id', auditId).set('X-Audit-Type', auditType);
        }
        //wimfra: adds baas header to request, when available in sessiondata.
        //this header will have preference to cookies, when being passed
        headers = this.#addBaasHeaders(headers);
        return headers;
    }
    #assignHeaders(headers, headersToAssign) {
        for (const [key, value] of Object.entries(headersToAssign)) {
            if (!headers.has(key))
                headers = headers.set(key, value);
        }
        return headers;
    }
    /**pass headers from session storage to call */
    #addBaasHeaders(headers) {
        if (this.#options.auth.contextTransportModus === BaasContextTransportModus.Header && this.#baasContextManager.checkBaasContextInStorage()) {
            const stateContext = this.#baasContextManager.getBaasContextFromStorage();
            // generate baas headers for auth
            const authHeaders = generateBaasAuthHeaders(stateContext);
            headers = this.#assignHeaders(headers, authHeaders);
        }
        const appStateHeaders = this.#transportHelper.generateAppStateHeaders();
        headers = this.#assignHeaders(headers, appStateHeaders);
        return headers;
    }
    #checkDomainRequest(url) {
        return (!checkUrlAbsolute(url) ||
            url.indexOf('localhost') > -1 ||
            url.startsWith(document.location.origin) ||
            _checkCompatibleOrigin(url, this.#options.compatibleOrigins));
    }
    /* check if this is a cross domain request
        rules:
        - xdc must be enabled (localhost, not production...)
        - we must be doing a call to the release domain (which is not localhost)
    */
    #checkCrossDomainRequest(url) {
        const result = false;
        if (this.#localhost) {
            // get the parts of the url being invoked by http
            const invokedUrlParts = extractUrlParts(url);
            // do the check if this is really a cross domain check (http call to origin of cross domain && cross domain is not localhost)
            return invokedUrlParts.origin == this.#state.location.release.origin && this.#state.location.release.hostname != 'localhost';
        }
        return result;
    }
    #handleAuthFailed(error) {
        this.#performExtraLogging('Auth Failed (455)', error.url, 'Performing log off.');
        this.#oopsDomService.showOops({ type: _OopsType.Authentication });
    }
    #handleForbidden(error) {
        this.#performExtraLogging('Forbidden (403)', error.url, 'Navigating to insufficient privileges page.');
        window.location.href = this.#state.location.baasStatic.url + BAR_CORE_CONSTANTS.PAGES.INSUFFICIENT_PRIVILEGES;
    }
    /** Define global variables related to interception */
    #defineGlobal() {
        // make sure the state is available globally for non Angular code
        const interception = ensureGlobal('lvflanders.bar.ng.core.interception');
        // here we define a global property to be used to turn on/off the fact that we handle auth failed responses
        Object.defineProperty(interception, 'handleAuthFailed', {
            set: (value) => {
                this.#handleAuthFailedStatus = value;
            },
            get: () => {
                return this.#handleAuthFailedStatus;
            },
            configurable: true
        });
        // here we define a global property to be used to turn on/off the fact that we handle auth failed responses
        Object.defineProperty(interception, 'handleForbidden', {
            set: (value) => {
                this.#handleForbiddenStatus = value;
            },
            get: () => {
                return this.#handleForbiddenStatus;
            },
            configurable: true
        });
    }
    #performExtraLogging(action, url, comment) {
        let message = `[BarCoreInterceptor] ${action} - ${url}`;
        if (comment)
            message += ` - ${comment}`;
        if (this.#state?.basiscontext)
            message += ` - context: ${JSON.stringify(this.#state?.basiscontext)}`;
        this.#logging.warn({ message });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarCoreInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarCoreInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarCoreInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

/** Angular service holding reactive state (signals) regarding the application.*/
class BaasCoreStateService {
    constructor() {
        this.#state = inject(_BarCoreStateService);
        this.basiscontext = this.#state.basiscontext.asReadonly();
        this.meldingen = this.#state.meldingen.asReadonly();
    }
    #state;
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasCoreStateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasCoreStateService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasCoreStateService, decorators: [{
            type: Injectable
        }] });
/** @deprecated Will be renamed with next major.
   Internal service which holds state of the BAR Angular core module within the single page.
   With this in place we can access the state anywhere.
 */
// NOTE not a model because injectable provider and not a service because it only holds state
class _BarCoreStateService {
    #coreService;
    constructor() {
        this.#coreService = inject(BarCoreService);
        /** Meldingen set by the BarMeldingGuard */
        this.meldingen = signal(undefined);
        this.basiscontext = signal(undefined);
        this.#coreService.basiscontextChanged.pipe(takeUntilDestroyed()).subscribe((basiscontext) => this.basiscontext.set(basiscontext));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BarCoreStateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BarCoreStateService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BarCoreStateService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

class BarMeldingenEngine {
    #state = inject(BaasAppState);
    #coreState = inject(_BarCoreStateService);
    #meldingService = inject(BaasMeldingService);
    /** Retrieves and processes the meldinen. Returns true in case the route is available (meaning no blocking meldingen). */
    async retrieveAndProcess(routeUrl) {
        const pathnameEndsWithSlash = this.#state.location.app.pathname.endsWith('/');
        const url = `${this.#state.location.app.pathname}${pathnameEndsWithSlash ? routeUrl.substring(1) : routeUrl}`;
        let meldingen;
        try {
            meldingen = await this.#meldingService.getMeldingen(url);
        }
        catch (err) {
            meldingen = [];
        }
        let onbeschikbaarMelding = null;
        if (meldingen && meldingen.length) {
            // Parse DTO to our model
            const parsedMeldingen = meldingen.map((m) => new BarMelding().convert(m));
            // Find melding of type onbeschikbaar
            onbeschikbaarMelding = parsedMeldingen.find((m) => m.meldingType === BarMeldingType.Onbeschikbaar);
            // If melding of type onbeschikbaar has been found, redirect to onbeschikbaar page
            if (onbeschikbaarMelding) {
                this.#handleOnbeschikbaar(onbeschikbaarMelding);
            }
            else {
                // Set meldingen on layout
                this.#coreState.meldingen.set(parsedMeldingen);
            }
        }
        else {
            this.#coreState.meldingen.set([]);
        }
        return onbeschikbaarMelding == null;
    }
    #handleOnbeschikbaar(melding) {
        const meldingKey = `${BAR_CORE_CONSTANTS.STORAGE_KEY_PREFIX}melding_${melding.id}`;
        // store melding content in session storage
        window.sessionStorage.setItem(meldingKey, JSON.stringify(melding));
        // redirect to onbeschikbaar pagina which will ready the melding to show from the session storage (and clean it up)
        window.location.href = `${this.#state.location.baasStatic.url}${BAR_CORE_CONSTANTS.PAGES.ONBESCHIKBAAR}/${melding.id}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarMeldingenEngine, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarMeldingenEngine }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarMeldingenEngine, decorators: [{
            type: Injectable
        }] });

function barMeldingGuard() {
    return (route, state) => {
        if (!route.routeConfig?.component)
            return true;
        const meldingEngine = inject(BarMeldingenEngine);
        return meldingEngine.retrieveAndProcess(state.url);
    };
}

// internal key in app store
const KEY_ENDPOINTS = '_endpoints';
/** Internal class logging all routes of the app. */
class _BarRouteLogger {
    #api = inject(BAAS_COMMON_API_CLIENT);
    #localhost = inject(BAR_LOCALHOST);
    #router = inject(Router);
    #state = inject(BaasAppState);
    #appStore = inject(BarCacheStore);
    #loggedEndpoints$$ = new BehaviorSubject(undefined);
    #destroyRef = inject(DestroyRef);
    ngOnDestroy() {
        this.#loggedEndpoints$$.complete();
    }
    init() {
        // behavior must be turned on and not in localhost modus
        const enabled = this.#state.config.baasStatic?.general?.endpointLoggingEnabled && !this.#localhost;
        if (!enabled)
            return;
        this.#router.events
            .pipe(filter((val) => val instanceof NavigationEnd), switchMap(() => {
            // Check if data is available in the BehaviorSubject, otherwise, fetch from appStore
            // NOTE this is to avoid having to access indexed db too much
            if (this.#loggedEndpoints$$.getValue() === undefined) {
                // also get the already logged endpoints from the storage
                return from(this.#appStore.getItem(KEY_ENDPOINTS)).pipe(tap((endpoints) => {
                    // Cache the fetched endpoints in the BehaviorSubject
                    this.#loggedEndpoints$$.next(endpoints);
                }));
            }
            else {
                return this.#loggedEndpoints$$.asObservable(); // Use cached data
            }
        }), map((loggedEndpoints) => {
            if (!loggedEndpoints)
                loggedEndpoints = [];
            const endpointsToLog = [];
            this.#collect(this.#router.config, endpointsToLog, loggedEndpoints);
            return { endpoints: endpointsToLog, loggedEndpoints };
        }), filter((result) => result.endpoints.length > 0), mergeMap$1((result) => {
            const remember$ = from(this.#appStore.setItem(KEY_ENDPOINTS, result.loggedEndpoints.concat(result.endpoints)));
            return forkJoin([from(this.#api.saveEndpoints(this.#state.toepassing.code, this.#state.toepassing.versie, result.endpoints)), remember$]);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }
    #collect(routes, endpointsToLog, loggedEndpoints, parentPath = undefined) {
        routes
            // don't collect framework routes
            .filter((route) => !route.data?._framework)
            .forEach((route) => {
            const fullPath = parentPath ? concatUrl(parentPath, route.path) : route.path;
            if (route.loadChildren) {
                // check that lazy loaded module has already been loaded
                if (route._loadedRoutes) {
                    // lazy loaded module, traverse the children
                    this.#collect(route._loadedRoutes, endpointsToLog, loggedEndpoints, fullPath);
                }
            }
            else {
                if (route.children?.length > 0) {
                    // this is a parent route, so traverse the children
                    this.#collect(route.children, endpointsToLog, loggedEndpoints, fullPath);
                }
                // define whether it is an end route
                const endRoute = route.component || route.loadComponent || route.redirectTo;
                // real route should have a component (but can still be a parent route)
                if (!route._baasHandled && endRoute) {
                    const path = concatUrl(this.#state.location.appRoot.pathname, fullPath);
                    // chech that it has not been logged yet
                    if (loggedEndpoints.indexOf(path) == -1) {
                        // regular route, just log in case not handled yet
                        endpointsToLog.push(path);
                    }
                    // mark as handled in case leaf route
                    // note that a parent route could always have lazy loaded modules which have not been loaded yet
                    route._baasHandled = true;
                }
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BarRouteLogger, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BarRouteLogger }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BarRouteLogger, decorators: [{
            type: Injectable
        }] });

/** Public BAR (initialization) service */
class BarStartupService {
    #injector = inject(Injector);
    #audit = inject(BaasAuditService);
    #state = inject(BaasAppState);
    #meetpunt = inject(BaasMeetpuntService);
    #initializedHooks = inject(BAR_INITIALIZED, { optional: true });
    #toepassing = inject(BaasToepassingService);
    #routeLogger = inject(_BarRouteLogger);
    #coreState = inject(_BarCoreStateService);
    #meldingEngine = inject(BarMeldingenEngine);
    #zone = inject(NgZone);
    #error = inject(ErrorHandler);
    #telemetry = inject(BAAS_TELEMETRY);
    #stopwatch;
    #telemetryGlobalSpan;
    // avoid cyclic DI error
    get #router() {
        return this.#injector.get(Router);
    }
    constructor() {
        // here we put some global stuff
        const core = ensureGlobal('lvflanders.bar.ng.core');
        // make global variable to handle the error from outside Angular
        core.handleError = (error) => {
            // run back in Angular zone from outside Angular
            this.#zone.run(() => {
                // invoke error routine
                this.#error.handleError(error);
            });
        };
        // handled unhanded errors with promises
        window.onunhandledrejection = (event) => this.#zone.run(() => {
            this.#error.handleError(event.reason);
        });
    }
    async load() {
        if (this.#telemetry.currentTransaction) {
            this.#telemetryGlobalSpan = this.#telemetry.currentTransaction.startSpan('Angular startup process (post @lvflanders/bar bootstrap)', 'ng-startup-process');
        }
        this.#coreState.basiscontext.set(this.#state.basiscontext);
        // init logging of routes
        this.#routeLogger.init();
        this.#enhanceRouting();
        // meldingen and audit stuff bound to routing
        this.#bindRouterEvents();
        // here we put some global stuff
        const core = ensureGlobal('lvflanders.bar.ng.core');
        // make sure the state is available globally for non Angular code
        core.state = this.#state;
        // put the toepassing service on global scope to open it up for outside user (such as puppeteer and web accessibility)
        core.toepassing = this.#toepassing;
        return lastValueFrom(this.#initialized());
    }
    /** Invoked when BAR is initialized. */
    #initialized() {
        const end = () => {
            if (this.#telemetryGlobalSpan)
                this.#telemetryGlobalSpan.end();
        };
        if (this.#initializedHooks != null) {
            return this.#executeInitializedHooks().pipe(tap$1(() => end()));
        }
        else {
            end();
            return of(undefined);
        }
    }
    #executeInitializedHooks() {
        const hookComparer = (a, b) => {
            // first let _framework internal property take precedence
            if (a._framework && !b._framework)
                return -1;
            if (!a._framework && b._framework)
                return 1;
            // second take into account the order
            if (a.order == null && b.order == null)
                return 0;
            if (a.order != null && b.order == null)
                return -1;
            if (a.order == null && b.order != null)
                return 1;
            if (a.order < b.order)
                return -1;
            if (a.order > b.order)
                return 1;
            return 0;
        };
        // sort the hooks by order, if no order it goes in the back of the list
        const sortedHooks = this.#initializedHooks.sort(hookComparer);
        let result$ = null;
        // NOTE concat didn't seem to work and forkJoin doesn't preserve the order
        // ensure everything is ran sequentially by modifying the observable till we reach the end of the array
        for (let i = 0; i < sortedHooks.length; i++) {
            const hookOutput$ = sortedHooks[i].run() || of(undefined);
            if (!result$)
                result$ = hookOutput$;
            else
                result$ = result$.pipe(mergeMap(() => hookOutput$));
        }
        return result$;
    }
    #bindRouterEvents() {
        // here we measure the routing time
        this.#router.events.subscribe(async (val) => {
            // on navigation start we start the stopwatch
            if (val instanceof NavigationStart) {
                // first reset the meldingen
                this.#coreState.meldingen.set(undefined);
                if (this.#stopwatch != null) {
                    this.#stopwatch.start();
                }
            }
            // on navigation end or error we stop the stopwatch
            else if (val instanceof NavigationEnd) {
                // meldingen are still undefined, meaning there was no meldingen guard, retrieve meldingen
                if (this.#coreState.meldingen() === undefined)
                    await this.#retrieveMeldingen(val.urlAfterRedirects || val.url);
                this.#endRouteWatch(val, 'ROUTER_NAVIGATION_END');
            }
            else if (val instanceof NavigationError) {
                this.#endRouteWatch(val, 'ROUTER_NAVIGATION_END');
            }
        });
    }
    #endRouteWatch(val, meetpunt) {
        // generate the route id
        const routeIdKey = 'routeId';
        this.#state[routeIdKey] = generateUuid();
        // audit this
        const audit = {
            auditId: this.#state.routeId,
            parentAuditId: this.#state.pageId,
            auditType: BaasAuditType.Route,
            details: {
                ROUTE: val.url,
                // also log the URL for completeness
                URL: document.location.href
            }
        };
        this.#audit.log(audit);
        _addAuditInfoOnDocument(audit);
        // do it in timeout so we executed meetpunt after (more accurate measurement)
        window.setTimeout(() => {
            // if no stopwatch it means meetpunten is not enabled
            if (this.#stopwatch != null) {
                this.#stopwatch.stop();
                this.#meetpunt.log({
                    meetpunt,
                    meetpuntDetail: val.url,
                    waarde: this.#stopwatch.duration,
                    meetpuntInfo: val.url,
                    auditId: this.#state.routeId
                });
            }
        }, 0);
    }
    /** Enhances routing of application by adding guards where needed. */
    #enhanceRouting() {
        if (this.#router.config) {
            this.#router.config
                // do not handle framework routes
                .filter((route) => route.data?._framework !== true)
                .forEach((route) => {
                if (route.children || route.loadChildren) {
                    if (!route.canActivateChild)
                        route.canActivateChild = [];
                    route.canActivateChild.push(barMeldingGuard());
                }
                else if (route.component) {
                    if (!route.canActivate)
                        route.canActivate = [];
                    route.canActivate.push(barMeldingGuard());
                }
            });
        }
    }
    async #retrieveMeldingen(routeUrl) {
        await this.#meldingEngine.retrieveAndProcess(routeUrl);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarStartupService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarStartupService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarStartupService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

// next will be called at application startup
function startupServiceFactory(startup) {
    // AOT-proof setup: https://github.com/angular/angular-cli/issues/5158
    const fn = () => startup.load();
    return fn;
}
/** Creates a factory which makes the bridge between non-angular @lvflanders/bar (InversifyJS) and Angular @lvflanders/bar-ng. */
function createBarContainerBridgeFactory(serviceIdentifier, container) {
    const result = () => container.get(serviceIdentifier);
    return result;
}
function createDefaultCacheStoreFactory(container, cacheStoreType) {
    const result = () => container.getTagged(BarCacheStore, BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, cacheStoreType);
    return result;
}
function userCacheStoreFactory(cacheManager, coreState) {
    // use lambda for user id because it can change over the lifetime of this application
    return cacheManager.getStore({
        storeType: BarCacheStoreType.User,
        userId: coreState.basiscontext.userId
    });
}
function meldingenCacheStoreFactory(cacheManager) {
    return cacheManager.getStore({ storeType: BarCacheStoreType.Global, store: 'meldingen' });
}
function notificationManagerFactory() {
    return new BarNotificationManager();
}
function defineAppProviders(container) {
    return [
        {
            provide: Container,
            useValue: container
        },
        {
            provide: BaasLoggingService,
            useFactory: createBarContainerBridgeFactory(BaasLoggingService, container)
        },
        // here we register our interceptor
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BarCoreInterceptor,
            multi: true
        },
        // next registration is the BAR service used at startup
        BarStartupService,
        {
            provide: APP_INITIALIZER,
            useFactory: startupServiceFactory,
            deps: [BarStartupService],
            multi: true
        },
        {
            provide: BaasAuthService,
            useFactory: createBarContainerBridgeFactory(BaasAuthService, container)
        },
        {
            provide: BaasAppState,
            useFactory: createBarContainerBridgeFactory(BaasAppState, container)
        },
        _BarCoreStateService,
        BaasCoreStateService,
        {
            provide: _BarAppInfoManager,
            useFactory: createBarContainerBridgeFactory(_BarAppInfoManager, container)
        },
        {
            provide: _BaasContextManager,
            useFactory: createBarContainerBridgeFactory(_BaasContextManager, container)
        },
        {
            provide: BaasAuditService,
            useFactory: createBarContainerBridgeFactory(BaasAuditService, container)
        },
        {
            provide: BaasToepassingService,
            useFactory: createBarContainerBridgeFactory(BaasToepassingService, container)
        },
        {
            provide: BaasSecurityService,
            useFactory: createBarContainerBridgeFactory(BaasSecurityService, container)
        },
        {
            provide: _BaasProfielService,
            useFactory: createBarContainerBridgeFactory(_BaasProfielService, container)
        },
        {
            provide: BaasConfigurationService,
            useFactory: createBarContainerBridgeFactory(BaasConfigurationService, container)
        },
        {
            provide: BaasMeetpuntService,
            useFactory: createBarContainerBridgeFactory(BaasMeetpuntService, container)
        },
        {
            provide: _BarXdc,
            useFactory: createBarContainerBridgeFactory(_BarXdc, container)
        },
        {
            provide: BaasMeldingService,
            useFactory: createBarContainerBridgeFactory(BaasMeldingService, container)
        },
        BarMeldingenEngine,
        // NOTE still used by @lvflanders/startscherm-ng-ui
        {
            provide: BarIdpClient,
            useFactory: createBarContainerBridgeFactory(BarIdpClient, container)
        },
        {
            provide: BarCacheManager,
            useFactory: createBarContainerBridgeFactory(BarCacheManager, container)
        },
        {
            provide: BarNotificationManager,
            useFactory: notificationManagerFactory
        },
        {
            // inject default cache store which is for the current app
            provide: BarCacheStore,
            useFactory: createDefaultCacheStoreFactory(container, BarCacheStoreType.Application),
            deps: [BarCacheManager]
        },
        {
            // inject current app cache
            provide: BAR_APP_CACHE,
            useFactory: createDefaultCacheStoreFactory(container, BarCacheStoreType.Application),
            deps: [BarCacheManager]
        },
        {
            // inject the user cache
            provide: BAR_USER_CACHE,
            useFactory: userCacheStoreFactory,
            deps: [BarCacheManager, BaasAppState]
        },
        {
            // inject the global cache
            provide: BAR_GLOBAL_CACHE,
            useFactory: createDefaultCacheStoreFactory(container, BarCacheStoreType.Global),
            deps: [BarCacheManager]
        },
        {
            provide: BAR_MELDINGEN_CACHE,
            useFactory: meldingenCacheStoreFactory,
            deps: [BarCacheManager]
        },
        // bridge to link the GVK state to the @lvflanders/bar-ng state
        {
            provide: BarGvkManager,
            useFactory: createBarContainerBridgeFactory(BarGvkManager, container)
        },
        {
            provide: BaasI18n,
            useFactory: createBarContainerBridgeFactory(BaasI18n, container)
        },
        {
            provide: BaasBootstrapOptions,
            useFactory: createBarContainerBridgeFactory(BaasBootstrapOptions, container)
        },
        _BarRouteLogger,
        {
            // NOTE Angular doesn't like symbols for ioc, can lead to strange errors in CDN modus
            provide: BAAS_COMMON_API_CLIENT,
            useFactory: createBarContainerBridgeFactory(BAAS_TYPES.BaasCommonApiClient, container)
        },
        {
            provide: _BaasStaticConfigurationApiClient,
            useFactory: createBarContainerBridgeFactory(_BaasStaticConfigurationApiClient, container)
        },
        {
            provide: _OopsDomService,
            useFactory: createBarContainerBridgeFactory(_OopsDomService, container)
        },
        {
            provide: _BaasTransportHelper,
            useFactory: createBarContainerBridgeFactory(_BaasTransportHelper, container)
        },
        {
            provide: BAR_LOCALHOST,
            useFactory: (options) => options.localhost,
            deps: [BaasBootstrapOptions]
        },
        // NOTE this makes the link with non angular error delegates (e.g. gismap)
        {
            provide: BAR_ERROR_HANDLER,
            useClass: BarErrorDelegateHandlerHook,
            multi: true
        },
        ...defineApmProviders(container)
    ];
}
function defineApmProviders(container) {
    return [
        {
            provide: BAAS_TELEMETRY,
            useFactory: createBarContainerBridgeFactory(BAAS_TYPES.BaasTelemetry, container)
        },
        {
            provide: _BaasApm,
            useFactory: createBarContainerBridgeFactory(_BaasApm, container)
        },
        _BaasApmService,
        {
            provide: BAR_INITIALIZED,
            useClass: BaasApmInitHook,
            multi: true
        }
    ];
}

// next is needed for case insensitive routing (added as provider in the forRoot method)
class LowerCaseUrlSerializer extends DefaultUrlSerializer {
    parse(url) {
        const urlTree = super.parse(url);
        this.#lowerCaseSegments(urlTree.root);
        return urlTree;
    }
    // https://stackoverflow.com/a/70879670
    #lowerCaseSegments(urlSegmentGroup) {
        if (urlSegmentGroup.hasChildren()) {
            Object.entries(urlSegmentGroup.children).forEach(([key, value]) => this.#lowerCaseSegments(value));
        }
        urlSegmentGroup.segments.forEach((segment) => (segment.path = segment.path.toLowerCase()));
    }
}
/** Provides the default configuration and providers for BaaS. */
function provideBaasCore(...features) {
    if (!features)
        features = [];
    // Providing services for the features sort by feature kind,
    // the order is important to avoid overriding providers by default providers
    // Sort the features based on the custom order
    features = features.sort((a, b) => {
        const orderA = _BAAS_CORE_FEATURE_ORDER[a.kind] ?? Number.MAX_VALUE; // Default to MAX_VALUE if not found
        const orderB = _BAAS_CORE_FEATURE_ORDER[b.kind] ?? Number.MAX_VALUE;
        return orderA - orderB;
    });
    // default providers
    const providers = [
        { provide: LOCALE_ID, useValue: LOCALE.FLEMISH },
        {
            provide: UrlSerializer,
            // routing needs to work case insensitive
            useClass: LowerCaseUrlSerializer
        },
        Location,
        {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        },
        {
            provide: ErrorHandler,
            useClass: BarErrorHandler
        },
        {
            provide: BAR_ERROR_HANDLER,
            useClass: BarErrorExpressionChangedAfterCheckedHook,
            multi: true
        },
        {
            provide: BAR_ERROR_HANDLER,
            useClass: BarErrorChunkLoadFailedHook,
            multi: true
        },
        {
            provide: ENVIRONMENT_INITIALIZER,
            multi: true,
            useValue: () => inject(BaasCoreInitService).init()
        },
        features.map((feature) => feature.providers)
    ];
    return makeEnvironmentProviders(providers);
}
/** @deprecated Use \@lvflanders/bar BaasBootstrapOptions.telemetry instead. This is not used anymore and doesn't do anything.
 * Angular options for APM. */
function withApm(options) {
    return {
        kind: BaasCoreFeatureKind.Apm,
        providers: [
            {
                provide: BAAS_APM_CONFIG,
                useValue: options
            }
        ]
    };
}
function withDateTimeInterception(conversionStrategy = BarDateTimeConversionStrategy.Iso) {
    return {
        kind: BaasCoreFeatureKind.DateTimeInterception,
        providers: [provideHttpClient(withInterceptors([barDateTimeInterceptor(conversionStrategy)]))]
    };
}
/** Provides the BaaS App providers which is statefull and invokes a backend api. */
function withBaasAppBehavior(appInstance) {
    return {
        kind: BaasCoreFeatureKind.BaasApp,
        providers: [
            // add the interceptors
            provideHttpClient(withInterceptors([baasTelemetryInterceptor]), withInterceptorsFromDi()),
            // in case connected we need to add extra providers
            ...defineAppProviders(appInstance.container)
        ]
    };
}
/** Whether the i18n features from \@lvflanders/bar should be integrated. Don't combine this with the function withBaasAppBehavior.*/
function withI18n(options = {}) {
    const i18n = new BaasI18n(options);
    return {
        kind: BaasCoreFeatureKind.I18n,
        providers: [{ provide: BaasI18n, useValue: i18n }]
    };
}

/** Internal class around window object for use within Angular context. */
class _BaasWindow {
    #ngZone = inject(NgZone);
    /** Sets a timeout on the window object outside angular to avoid change detection cycles because of zonejs. */
    setTimeoutOutsideAngular(handler, timeout) {
        return new Promise((resolve) => {
            this.#ngZone.runOutsideAngular(() => {
                const result = window.setTimeout(handler, timeout);
                resolve(result);
            });
        });
    }
    setIntervalOutsideAngular(handler, timeout) {
        return new Promise((resolve) => {
            this.#ngZone.runOutsideAngular(() => {
                const result = window.setInterval(handler, timeout);
                resolve(result);
            });
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BaasWindow, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BaasWindow, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: _BaasWindow, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class BaasFileService {
    #http = inject(HttpClient);
    /** Downloads a file through http and saves it.
     * For the filename you might have to add a header in the aspnet core action like so: Response.Headers["Access-Control-Expose-Headers"] =  new StringValues("Content-Disposition");
     * Otherwise the JS/Angular code might not be able to read the filename from the headers (cors).
     */
    async download(
    /** Url of file (action).*/ url, 
    /* Optional client-side filename. By default an attempt is done to read the server-side filename. */ filename = undefined) {
        const response = await lastValueFrom(this.#http.get(url, { observe: 'response', responseType: 'blob' }));
        if (!response.body || response.body.size === 0) {
            console.warn('No data returned from server');
            return;
        }
        if (!filename) {
            filename = response.headers.get('content-disposition')?.split('filename=')[1]?.split(';')[0];
            // trim quotes to avoid files starting and ending with _
            filename = filename?.replace(/^"|"$/g, '');
        }
        saveAs(response.body, filename);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasFileService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasFileService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaasFileService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/** Creates a validator which compares the given control to another control with a comparison type in case both controls are filled in.
    The error key is the comparison type.
 */
function createComparisonValidator(otherControl, comparison) {
    return (control) => {
        const otherValue = otherControl.value;
        if (control.value && otherValue) {
            switch (comparison) {
                case 'equal': {
                    if (control.value != otherValue)
                        return { equal: true };
                    break;
                }
                case 'larger': {
                    if (control.value <= otherValue)
                        return { larger: true };
                    break;
                }
                case 'largerOrEqual': {
                    if (control.value < otherValue)
                        return { largerOrEqual: true };
                    break;
                }
                case 'smaller': {
                    if (control.value >= otherValue)
                        return { smaller: true };
                    break;
                }
                case 'smallerOrEqual': {
                    if (control.value > otherValue)
                        return { smallerOrEqual: true };
                    break;
                }
                default:
                    return null;
            }
        }
        return null;
    };
}
/** @experimental Creates a validator which compares the given date control to another date control with a comparison type in case both controls are filled in.
    Date-fns is used internally.
    The error key is the comparison type.
 */
function createDateComparisonValidator(otherControl, comparison) {
    return (control) => {
        const otherValue = otherControl?.value;
        if (control?.value && otherValue) {
            switch (comparison) {
                case 'equal': {
                    if (!isEqual(control.value, otherValue))
                        return { equal: true };
                    break;
                }
                case 'larger': {
                    if (!isAfter(control.value, otherValue))
                        return { larger: true };
                    break;
                }
                case 'largerOrEqual': {
                    if (isBefore(control.value, otherValue))
                        return { largerOrEqual: true };
                    break;
                }
                case 'smaller': {
                    if (!isBefore(control.value, otherValue))
                        return { smaller: true };
                    break;
                }
                case 'smallerOrEqual': {
                    if (isAfter(control.value, otherValue))
                        return { smallerOrEqual: true };
                    break;
                }
                default:
                    return null;
            }
        }
        return null;
    };
}

// Public functions
/** Creates a validator with a given error key and callback to check for validity. */
function createGenericValidator(errorKey, checkValid) {
    return (control) => {
        const valid = checkValid(control);
        if (valid)
            return null;
        else {
            const result = {};
            result[errorKey] = { value: control.value };
            return result;
        }
    };
}
function checkTruthy(value, truthyCheckMode) {
    switch (truthyCheckMode) {
        case 'truthy':
            return value; // Default truthy check
        case 'notNullOrUndefined':
            return value !== null && value !== undefined;
        case 'notNull':
            return value !== null;
        case 'notUndefined':
            return value !== undefined;
    }
}
/** Creates a validators where the given control value is required in case a given condition is true. */
function createRequiredIfValidator(ifCondition, truthyCheckMode = 'truthy') {
    const checkValid = (control) => {
        return !ifCondition(control) || checkTruthy(control?.value, truthyCheckMode);
    };
    return createGenericValidator('required', checkValid);
}
/** Creates a validators where the given control value must be larger than the value of another given control. */
function createGreaterThanControlValidator(otherControl) {
    return createCompareControlValidator('greaterThan', otherControl, (value, otherValue) => value > otherValue);
}
/** Creates a validators where the given control value must be larger than or equal to the value of another given control. */
function createGreaterThanOrEqualControlValidator(otherControl) {
    return createCompareControlValidator('greaterThanOrEqual', otherControl, (value, otherValue) => value >= otherValue);
}
/** Creates a validators where the given control value must be smaller than the value of another given control. */
function createSmallerThanControlValidator(otherControl) {
    return createCompareControlValidator('smallerThan', otherControl, (value, otherValue) => value < otherValue);
}
/** Creates a validators where the given control value must be smaller than or equal the value of another given control. */
function createSmallerThanOrEqualControlValidator(otherControl) {
    return createCompareControlValidator('smallerThanOrEqual', otherControl, (value, otherValue) => value <= otherValue);
}
// Facade
// NOTE inspired by Validators class from @angular
/** BAR reactive forms validators. */
class BarValidators {
    /** Creates a validators where the given control value is required in case a given condition is true. */
    static requiredIf(ifCondition, truthyCheckMode = 'truthy') {
        return createRequiredIfValidator(ifCondition, truthyCheckMode);
    }
    /** Creates a validators where the given control value must be larger than the value of another given control. */
    static greaterThanControl(otherControl) {
        return createGreaterThanControlValidator(otherControl);
    }
    /** Creates a validators where the given control value must be larger than or equal to the value of another given control. */
    static greaterThanOrEqualControl(otherControl) {
        return createGreaterThanOrEqualControlValidator(otherControl);
    }
    /** Creates a validators where the given control value must be smaller than the value of another given control. */
    static smallerThanControl(otherControl) {
        return createSmallerThanControlValidator(otherControl);
    }
    /** Creates a validators where the given control value must be smaller than or equal the value of another given control. */
    static smallerThanOrEqualControl(otherControl) {
        return createSmallerThanOrEqualControlValidator(otherControl);
    }
    /** @experimental Creates a validators where the given control date value is compared to to another typed date control using date-fns. */
    static dateComparison(otherControl, comparison) {
        return createDateComparisonValidator(otherControl, comparison);
    }
    /** Creates a validators where the given control value is compared to to another control. */
    static comparison(otherControl, comparison) {
        return createComparisonValidator(otherControl, comparison);
    }
}
// Private functions
function controlValueDefined(control) {
    return control?.value != null;
}
function createCompareControlValidator(errorKey, otherControl, comparisonCondition) {
    return createGenericValidator(errorKey, (control) => {
        // both control values must be defined in able to perform the condition (otherwise it is considered value)
        return !controlValueDefined(control) || !controlValueDefined(otherControl) || comparisonCondition(control.value, otherControl.value);
    });
}

/*
 * Public API Surface of @lvflanders/bar-ng
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BAAS_APM_CONFIG, BAAS_TELEMETRY, BAR_APP_CACHE, BAR_ERROR_HANDLER, BAR_GLOBAL_CACHE, BAR_INITIALIZED, BAR_LOCALHOST, BAR_MELDINGEN_CACHE, BAR_USER_CACHE, BaasCoreStateService, BaasFileService, BarActieFromContext, BarActionDef, BarCacheable, BarCacheableOptions, BarCoreService, BarDateTimeConversionStrategy, BarHttpParams, BarMeetpunt, BarMelding, BarMeldingType, BarRoutingService, BarValidators, _BaasWindow, _BarCoreStateService, barActieAllowedGuard, barAuthGuard, barZoneGuard, convertToHttpParams, createComparisonValidator, createDateComparisonValidator, createDefaultBaseHrefFactory, createGenericValidator, createGreaterThanControlValidator, createGreaterThanOrEqualControlValidator, createPageNotFoundRoute, createRequiredIfValidator, createSmallerThanControlValidator, createSmallerThanOrEqualControlValidator, provideBaasCore, resolveSignalValue, resolveValue$, withApm, withBaasAppBehavior, withDateTimeInterception, withI18n };
//# sourceMappingURL=lvflanders-bar-ng.mjs.map
